import React from "react";
import { Box, useTheme, Typography } from "@mui/material";
import ReactFlow, { Background, Controls, MiniMap, Handle, Position } from "reactflow";
import WorkflowNode from "./WorkflowNode";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

function EndNode({ data }) {
  const leftPosition = data?.isAfterOptionNode ? 15 : 84;
  
  return (
    <Box sx={{
      background: '#b6f7b0',
      color: '#222',
      borderRadius: 2,
      px: 4,
      py: 0.5,
      maxWidth: 50,
      minHeight: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 600,
      fontSize: 16,
      boxShadow: 1,
      border: '1.5px solid #7ed957',
      position: 'relative',
      left: leftPosition
    }}>
      <Handle type="target" position={Position.Top} style={{ background: 'transparent', border: 'none' }} />
      End
    </Box>
  );
}

// Read-only edge component (no plus buttons)
function ReadOnlyEdge({ id, sourceX, sourceY, targetX, targetY, style = {}, markerEnd, data }) {
  const isHorizontalBranch = data?.isHorizontalBranch || Math.abs(targetX - sourceX) > Math.abs(targetY - sourceY);
  const isNoReplyConnection = data?.branchId?.startsWith('no-reply-');

  let edgePath;

  if (isHorizontalBranch) {
    const midY1 = sourceY + 50;
    const midY2 = targetY - 50;
    const midX = targetX;
    edgePath = `M ${sourceX} ${sourceY} L ${sourceX} ${midY1} L ${midX} ${midY1} L ${midX} ${midY2} L ${targetX} ${targetY}`;
  } else {
    // Use straight path for vertical edges
    edgePath = `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
  }

  return (
    <path
      id={id}
      style={{
        ...style,
        strokeWidth: isHorizontalBranch ? 2 : 1,
        strokeDasharray: isNoReplyConnection ? "8,4" : (isHorizontalBranch ? "5,5" : "none"),
        stroke: isNoReplyConnection ? "#999" : style.stroke,
      }}
      className="react-flow__edge-path"
      d={edgePath}
      markerEnd={markerEnd}
    />
  );
}

// Define nodeTypes and edgeTypes outside component to prevent recreation on every render
const nodeTypes = {
  workflowNode: WorkflowNode,
  end: EndNode,
};

const edgeTypes = {
  readonly: ReadOnlyEdge,
};

const VersionViewer = ({ version }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  // Use the provided version's branches instead of selectedWorkflow
  const branches = version?.branches || { start: [] };

  // Recursively build nodes and edges from branches (same logic as ActionsTab but read-only)
  let nodes = [];
  let edges = [];
  const centerX = 400;
  const branchYSpacing = 200;
  const nodeYSpacing = 180;
  const optionXSpacing = 500;
  let branchPositions = { start: { x: centerX, y: 20 } };

  function traverseBranch(branchId, x, y, parentNodeId = null, isAfterOptionNode = false) {
    const branch = branches[branchId] || [];
    let lastNodeId = null;
    let lastNodeHasNext = false;
    let lastNodeWasOption = false;
    
    branch.forEach((node, idx) => {
      const nodeId = `${branchId}-${node.id || idx}`;
      let icon, color, heading, description;
      
      // Check if this is an option node (only the first node in an option branch or no-reply branch)
      const isOptionNode = (branchId.startsWith('option-') || branchId.startsWith('keyword-') || branchId.startsWith('no-reply-')) && idx === 0;
      
      // Check if the previous node in this branch was an option node
      const isAfterOptionNodeInBranch = idx > 0 && lastNodeWasOption;
      
      if (node.type === 'trigger') {
        icon = <CenterFocusStrongIcon sx={{ fontSize: 22, mr: 1, color: "#222" }} />;
        color = "#B2F2F6";
        heading = node.trigger?.name || "Trigger";
        description = node.trigger?.description || "";
      } else if (isOptionNode) {
        // Style option nodes as small, compact cards
        icon = null; // No icon for option nodes
        color = "#E8E8E8"; // Light gray background
        heading = node.action?.name || "Option";
        description = ""; // No description for option nodes
      } else {
        icon = <ChatBubbleOutlineIcon sx={{ color: "#13795B", mr: 1 }} />;
        color = "#B2F2D6";
        heading = node.action?.name || "Action";
        description = node.action?.description || "";
      }
      
      nodes.push({
        id: nodeId,
        type: "workflowNode",
        data: {
          heading,
          description,
          icon,
          color,
          type: node.type,
          config: node.trigger?.config || node.action?.config,
          stepRaw: node,
          branchId,
          nodeId: node.id || idx,
          isOptionNode, // Add flag to identify option nodes
          isAfterOptionNode: isAfterOptionNodeInBranch, // Check if previous node in this branch was an option
          isReadOnly: true, // Mark as read-only
        },
        position: { x, y: y + idx * nodeYSpacing },
      });
      
      if (lastNodeId) {
        edges.push({
          id: `${lastNodeId}-to-${nodeId}`,
          source: lastNodeId,
          target: nodeId,
          type: "readonly", // Use readonly edge type
          data: { branchId, nodeIndex: idx },
        });
      }
      
      lastNodeId = nodeId;
      lastNodeWasOption = isOptionNode;
      
      // Check if this node has next branches (horizontal branching)
      if (Array.isArray(node.next) && node.next.length > 0) {
        lastNodeHasNext = true;
        
        // Calculate horizontal positions for option nodes
        const totalWidth = (node.next.length - 1) * optionXSpacing;
        const startX = x - totalWidth / 2;
        const branchY = y + (idx + 1) * nodeYSpacing + branchYSpacing;
        
        // Create edges from this node to each option node with horizontal routing
        node.next.forEach((nextBranchId, bIdx) => {
          const nextBranch = branches[nextBranchId] || [];
          if (nextBranch.length > 0) {
            const nextNodeId = `${nextBranchId}-${nextBranch[0].id || 0}`;
            
            edges.push({
              id: `${nodeId}-to-${nextNodeId}`,
              source: nodeId,
              target: nextNodeId,
              type: "readonly", // Use readonly edge type
              data: { 
                branchId: nextBranchId, 
                nodeIndex: 0,
                isHorizontalBranch: true, // Flag for horizontal branching
                branchIndex: bIdx,
                totalBranches: node.next.length
              },
            });
          }
          
          // Calculate position for the next branch - position horizontally
          const branchX = startX + bIdx * optionXSpacing;
          branchPositions[nextBranchId] = { x: branchX, y: branchY };
          
          // Recursively traverse the next branch
          traverseBranch(nextBranchId, branchX, branchY, nodeId, false);
        });
      }
    });
    
    // Only add End node if the last node doesn't have any next branches
    if (branch.length > 0 && !lastNodeHasNext) {
      const lastNodeId = `${branchId}-${branch[branch.length - 1].id || (branch.length - 1)}`;
      const endNodeId = `${lastNodeId}-endnode`;
      
      // Add an EndNode below the last node
      nodes.push({
        id: endNodeId,
        type: 'end',
        data: { isAfterOptionNode: lastNodeWasOption }, // Pass whether the last node was an option
        position: { x, y: y + branch.length * nodeYSpacing + 100 }, // 100px below last node
        draggable: false,
        selectable: false,
      });
      
      edges.push({
        id: `${lastNodeId}-to-end`,
        source: lastNodeId,
        target: endNodeId,
        type: "readonly", // Use readonly edge type
        data: { branchId, nodeIndex: branch.length },
      });
    }
  }

  // Start traversal from 'start' branch
  traverseBranch('start', centerX, 20);

  // Determine if there are any nodes in the workflow
  const hasBranches = branches && Object.keys(branches).length > 0;
  const hasStartBranch = hasBranches && Array.isArray(branches.start) && branches.start.length > 0;

  // If no branches or empty start branch, show empty state
  if (!hasBranches || !hasStartBranch) {
    return (
      <Box sx={{ 
        height: "100%", 
        width: "100%", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        background: isDark ? "#181818" : "#fff"
      }}>
        <Typography variant="h6" color="text.secondary">
          This version has no workflow steps
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        minHeight: 0,
        flex: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        style={{ background: isDark ? "#181818" : "#fff", height: "100%", width: "100%", minHeight: 0 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        minZoom={0.5}
        maxZoom={1.5}
      >
        <Background
          color={isDark ? "#222" : "#eee"}
          gap={16}
          variant="lines"
        />
        <Controls
          style={{
            background: isDark ? "#23272b" : "#fff",
            color: isDark ? "#fff" : "#222",
            border: `1px solid ${isDark ? "#444" : "#ccc"}`,
            boxShadow: isDark ? "0 2px 8px #0008" : "0 2px 8px #8882",
            borderRadius: 8,
            top: 16,
            right: 16,
            left: "auto",
            bottom: "auto",
            zIndex: 10,
          }}
          showInteractive={false}
        />
        <MiniMap
          nodeColor={(n) => n.data?.color || (isDark ? "#23272b" : "#B2F2D6")}
          maskColor={isDark ? "rgba(40,40,40,0.7)" : "rgba(200,200,200,0.6)"}
          style={{
            position: "absolute",
            right: 16,
            bottom: 16,
            background: isDark ? "#23272b" : "#fff",
            border: `1.5px solid ${isDark ? "#444" : "#ccc"}`,
            borderRadius: 8,
            boxShadow: isDark ? "0 2px 8px #0008" : "0 2px 8px #8882",
            zIndex: 11,
            width: 140,
            height: 100,
          }}
        />
      </ReactFlow>
    </Box>
  );
};

export default VersionViewer; 